import { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface Material {
  id: number;
  title: string;
  type: 'video' | 'quiz' | 'document' | 'case-study';
  role: string;
  team: string;
  riskLevel: number;
  completed: boolean;
  dateAdded: string;
  description: string;
}

const materials: Material[] = [
  {
    id: 1,
    title: 'Crisis Response Fundamentals',
    type: 'video',
    role: 'First Responder',
    team: 'CCRT',
    riskLevel: 1,
    completed: false,
    dateAdded: '2024-01-15',
    description: 'Essential training for crisis response team members',
  },
  {
    id: 2,
    title: 'Incident Management Quiz',
    type: 'quiz',
    role: 'SEIC',
    team: 'CCMT',
    riskLevel: 2,
    completed: true,
    dateAdded: '2024-01-10',
    description: 'Test your knowledge of incident management procedures',
  },
  {
    id: 3,
    title: 'Crisis Communication Guidelines',
    type: 'document',
    role: 'EIC',
    team: 'Communications',
    riskLevel: 3,
    completed: false,
    dateAdded: '2024-01-05',
    description: 'Best practices for crisis communication',
  },
  {
    id: 4,
    title: 'Ransomware Response Case Study',
    type: 'case-study',
    role: 'LIH',
    team: 'CSIRT',
    riskLevel: 1,
    completed: true,
    dateAdded: '2024-01-01',
    description: 'Analysis of a successful ransomware incident response',
  },
];

const roles = [
  'All Roles',
  'SEIC',              // Senior Executive-in-Charge
  'EIC',               // Executive-in-Charge
  'LIH',               // Lead Incident Handler
  'IRL',               // Incident Response Lead
  'CCST Lead',         // Cyber Crisis Support Team Lead
  'CSIRT Lead',        // Computer Security Incident Response Team Lead
  'First Responder',
  'Support Staff'
];

const teams = [
  'All Teams',
  'CCET',              // Cyber Crisis Executive Team
  'CCMT',              // Cyber Crisis Management Team
  'CCRT',              // Cyber Crisis Response Team
  'CCST',              // Cyber Crisis Support Team
  'CSIRT',             // Computer Security Incident Response Team
  'Communications',    // Communications Team
  'Legal',             // Legal Team
  'Security',          // Security Team
  'Operations'         // Operations Team
];

const riskLevels = [0, 1, 2, 3, 4];
const sortOptions = [
  { id: 'relevant', name: 'Most Relevant' },
  { id: 'recent', name: 'Recently Added' },
  { id: 'completion', name: 'Completion Status' },
];

export default function LearningMaterials() {
  const [query, setQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [selectedTeam, setSelectedTeam] = useState('All Teams');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState(0);
  const [sortBy, setSortBy] = useState('relevant');

  const filteredMaterials = useMemo(() => {
    return materials
      .filter((material) => {
        const matchesQuery = material.title.toLowerCase().includes(query.toLowerCase()) ||
                           material.description.toLowerCase().includes(query.toLowerCase());
        const matchesRole = selectedRole === 'All Roles' || material.role === selectedRole;
        const matchesTeam = selectedTeam === 'All Teams' || material.team === selectedTeam;
        const matchesRiskLevel = selectedRiskLevel === 0 || material.riskLevel === selectedRiskLevel;
        
        return matchesQuery && matchesRole && matchesTeam && matchesRiskLevel;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'recent':
            return new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
          case 'completion':
            return Number(b.completed) - Number(a.completed);
          default:
            return 0;
        }
      });
  }, [query, selectedRole, selectedTeam, selectedRiskLevel, sortBy]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return VideoCameraIcon;
      case 'quiz':
        return AcademicCapIcon;
      case 'document':
        return DocumentTextIcon;
      case 'case-study':
        return BookOpenIcon;
      default:
        return DocumentTextIcon;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Search learning materials..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {roles.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>

            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {teams.map((team) => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>

            <select
              value={selectedRiskLevel}
              onChange={(e) => setSelectedRiskLevel(Number(e.target.value))}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              <option value={0}>All Risk Levels</option>
              {riskLevels.slice(1).map((level) => (
                <option key={level} value={level}>Level {level}</option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {sortOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Results */}
        <div className="mt-6 space-y-4">
          {filteredMaterials.map((material) => {
            const TypeIcon = getTypeIcon(material.type);
            return (
              <div
                key={material.id}
                className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0">
                  <TypeIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900 truncate">{material.title}</h4>
                    <span
                      className={clsx(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        material.completed
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      )}
                    >
                      {material.completed ? 'Completed' : 'Not Started'}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">{material.description}</p>
                  <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                    <span>Role: {material.role}</span>
                    <span>Team: {material.team}</span>
                    <span>Risk Level: {material.riskLevel}</span>
                    <span>Added: {material.dateAdded}</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">
                    {material.completed ? 'Review' : 'Start'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}